import React, { useState, useEffect } from 'react';
import { NAV_SECTIONS } from './config/pages';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import api from './api';
import { useTheme } from './ThemeContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import WorkOrders from './pages/WorkOrders';
import Clients from './pages/Clients';
import Team from './pages/Team';
import Monitoring from './pages/Monitoring';
import KPI from './pages/KPI';
import Performance from './pages/Performance';
import ContentCalendar from './pages/ContentCalendar';
import Costs from './pages/Costs';
import Scheduling from './pages/Scheduling';
import Creatives from './pages/Creatives';
import PostCopy from './pages/PostCopy';
import VideoWorks from './pages/VideoWorks';
import PrintMedia from './pages/PrintMedia';
import MasterData, { seedStandardMasterData } from './pages/MasterData';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AccessControl from './pages/AccessControl';
import WorkAssignments from './pages/WorkAssignments';
import Touchpoints from './pages/Touchpoints';
import Approvals from './pages/Approvals';
import Chat from './pages/Chat';
import Pipeline from './pages/Pipeline';
import Planning from './pages/Planning';
import Reallocation from './pages/Reallocation';
import SpecialDays from './pages/SpecialDays';
import PublishedContent from './pages/PublishedContent';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CancelDeletion from './pages/CancelDeletion';
import DaySelect from "./pages/DaySelect";
import ReviewApproval from "./pages/ReviewApproval";

const PAGE_META = {
  '/dashboard':      { title: 'Dashboard',            sub: 'Digital Marketing Operations',            icon: '▦' },
  '/special-days':   { title: 'Special Days',          sub: 'Client special day selections',           icon: '🎉' },
  '/planning':       { title: 'Monthly Planner',       sub: 'Plan your monthly content',               icon: '⊞' },
  '/work-orders':    { title: 'Work Orders',           sub: 'Manage all campaign work orders',         icon: '≡' },
  '/pipeline':       { title: 'WO Pipeline',           sub: 'Track workflow phases',                   icon: '⋯' },
  '/content':        { title: 'Content Briefs',        sub: 'Create, plan and approve content',        icon: '☰' },
  '/postcopy':       { title: 'Post Copy',             sub: 'Platform captions and hashtags',          icon: '✎' },
  '/videoworks':     { title: 'Video Works',           sub: 'Video production tracking and delivery',   icon: '▶' },
  '/printmedia':     { title: 'Print Media',        sub: 'Print design tracking and delivery',      icon: '🖨' },
  '/creatives':      { title: 'Creatives',             sub: 'Design briefs and asset approvals',       icon: '⬡' },
  '/scheduling':        { title: 'Scheduling & Posting',  sub: 'Schedule and confirm posts',              icon: '↗' },
  '/published-content': { title: 'Published Content',    sub: 'All posted work orders with live links',   icon: '📰' },
  '/monitoring':     { title: 'Monitoring',            sub: 'Post-publish observations and metrics',   icon: '◎' },
  '/assignments':    { title: 'Work Assignments',      sub: 'Tasks and workload by team member',       icon: '⊡' },
  '/reallocation':   { title: 'Dynamic Reallocation',  sub: 'Reassign tasks when team is absent',     icon: '⇄' },
  '/touchpoints':    { title: 'Touchpoints',           sub: 'Client communication log',                icon: '◇' },
  '/approvals':     { title: 'Approvals',           sub: 'Review and approve pending items',        icon: '✓' },
  '/chat':           { title: 'Chat',                sub: 'Work order messaging',                    icon: '💬' },
  '/costs':          { title: 'Cost Tracking',         sub: 'Budget vs actual spend',                  icon: '◈' },
  '/kpi':            { title: 'KPI Tracker',           sub: 'Live metrics from monitoring data',       icon: '▲' },
  '/performance':    { title: 'Performance',           sub: 'Team performance from task data',         icon: '★' },
  '/team':           { title: 'Team Availability',     sub: 'Current availability and workload',       icon: '⊕' },
  '/clients':        { title: 'Clients',               sub: 'Client master list',                      icon: '⊙' },
  '/masterdata':     { title: 'Master Data',           sub: 'Configure workflows and settings',        icon: '⊗' },
  '/settings':       { title: 'Settings',              sub: 'Agency branding and preferences',         icon: '⚙' },
  '/billing':        { title: 'Subscription & Billing', sub: 'Your plan, limits and upgrade options',    icon: '◈' },
  '/access-control': { title: 'Access Control',        sub: 'Role-based permissions',                  icon: '⊛' },
};

// ── Nav Icons — colored to match sample screenshot ────────────────────────────
function Icon({ d, color, size=18, fill='none', sw=1.8, children }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      {d && <path d={d}/>}
      {children}
    </svg>
  );
}

const NAV_ICONS = {
  // purple/indigo — home icon
  dashboard: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#7c5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9,22 9,12 15,12 15,22"/>
    </svg>
  ),
  // blue — calendar with lines
  planner: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <line x1="8" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="13" y2="18"/>
    </svg>
  ),
  // blue — calendar with check
  content: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M9 16l2 2 4-4"/>
    </svg>
  ),
  // orange — image/photo icon
  creatives: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21,15 16,10 5,21"/>
    </svg>
  ),
  // amber — edit/pen icon
  postcopy: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  // red — video camera icon
  videoworks: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23,7 16,12 23,17"/>
      <rect x="1" y="5" width="15" height="14" rx="2"/>
    </svg>
  ),
  // purple — print/document icon
  printmedia: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#8b5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9V2h12v7"/>
      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
      <rect x="6" y="14" width="12" height="8"/>
    </svg>
  ),
  // teal — send/rocket icon
  scheduling: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#14b8a6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22,2 15,22 11,13 2,9"/>
    </svg>
  ),
  // green — newspaper/published content icon
  published: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2"/>
      <line x1="10" y1="7" x2="18" y2="7"/>
      <line x1="10" y1="11" x2="18" y2="11"/>
      <line x1="10" y1="15" x2="14" y2="15"/>
    </svg>
  ),
  // purple — activity/pulse icon
  monitoring: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#9b5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
    </svg>
  ),
  // blue — git branch/pipeline
  pipeline: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="2"/>
      <circle cx="18" cy="6" r="2"/>
      <circle cx="18" cy="18" r="2"/>
      <path d="M6 8v2a2 2 0 002 2h8a2 2 0 012-2v0"/>
      <line x1="18" y1="8" x2="18" y2="16"/>
    </svg>
  ),
  // indigo — clipboard list
  assignments: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="9" y1="16" x2="12" y2="16"/>
    </svg>
  ),
  // indigo — layers/stack
  workorders: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12,2 2,7 12,12 22,7"/>
      <polyline points="2,17 12,22 22,17"/>
      <polyline points="2,12 12,17 22,12"/>
    </svg>
  ),
  // blue — users icon
  team: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87"/>
      <path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  // blue — shuffle/swap
  realloc: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16,3 21,3 21,8"/>
      <line x1="4" y1="20" x2="21" y2="3"/>
      <polyline points="21,16 21,21 16,21"/>
      <line x1="15" y1="15" x2="21" y2="21"/>
    </svg>
  ),
  // blue — message circle
  touchpoints: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
    </svg>
  ),
  // orange — indian rupee / coin
  costs: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M9 8h6M9 12h6M13 16l-4-4"/>
      <line x1="9" y1="12" x2="9" y2="16"/>
    </svg>
  ),
  // red/pink — bar chart
  kpi: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
      <line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  // amber — award/medal
  performance: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="9" r="6"/>
      <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"/>
    </svg>
  ),
  // indigo — database/grid
  masterdata: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
  // indigo — settings/sliders
  settings: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6"/>
      <line x1="4" y1="12" x2="20" y2="12"/>
      <line x1="4" y1="18" x2="20" y2="18"/>
      <circle cx="9" cy="6" r="2" fill="#6366f1"/>
      <circle cx="15" cy="12" r="2" fill="#6366f1"/>
      <circle cx="9" cy="18" r="2" fill="#6366f1"/>
    </svg>
  ),
  // grey-blue — shield/lock
  // green — checkmark circle (approvals)
  approvals: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
  // teal — chat bubble icon
  chat: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#14b8a6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
    </svg>
  ),
  access: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  // grey-blue — building
  clients: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/>
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
      <line x1="12" y1="12" x2="12" y2="16"/>
      <line x1="10" y1="14" x2="14" y2="14"/>
    </svg>
  ),
  // credit-card — billing
  billing: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  // topbar icons
  bell: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
};

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', flexDirection:'column', gap:12 }}>
      <div style={{ width:36, height:36, borderRadius:'50%', border:'3px solid var(--border)', borderTopColor:'var(--accent)', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{ color:'var(--text3)', fontSize:'0.82rem', fontFamily:'DM Sans,sans-serif' }}>Loading Leadex…</span>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  return children;
}

function NavSection({ label, children, collapsed }) {
  return (
    <div style={{ padding:'14px 0 2px' }}>
      {!collapsed && (
        <div style={{ fontSize:'0.62rem', fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'#9ca3af', padding:'0 16px', marginBottom:4 }}>
          {label}
        </div>
      )}
      {collapsed && <div style={{ height:1, background:'var(--border)', margin:'0 8px 6px' }} />}
      {children}
    </div>
  );
}

function NavItem({ to, icon, label, onClick, collapsed }) {
  return (
    <NavLink to={to} onClick={onClick}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display:'flex', alignItems:'center', gap: collapsed ? 0 : 10,
        padding: collapsed ? '8px 0' : '7px 12px',
        margin: collapsed ? '1px 4px' : '1px 8px',
        borderRadius:8,
        cursor:'pointer',
        justifyContent: collapsed ? 'center' : 'flex-start',
        color: isActive ? '#3b7ef0' : '#374151',
        fontSize:'0.82rem',
        fontWeight: isActive ? 600 : 400,
        textDecoration:'none',
        transition:'all 0.2s',
        background: isActive ? 'rgba(59,126,240,0.08)' : 'transparent',
        position:'relative',
      })}>
      {({ isActive }) => (
        <>
          <span style={{
            width:20, height:20,
            display:'flex', alignItems:'center', justifyContent:'center',
            flexShrink:0,
            opacity: isActive ? 1 : 0.75,
            transition:'opacity 0.15s',
          }}>
            {icon}
          </span>
          {!collapsed && <span style={{ flex:1, overflow:'hidden', whiteSpace:'nowrap' }}>{label}</span>}
          {!collapsed && isActive && (
            <svg viewBox="0 0 6 10" width="6" height="10" fill="none" stroke="#3b7ef0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 1l4 4-4 4"/>
            </svg>
          )}
        </>
      )}
    </NavLink>
  );
}


function TrialBanner() {
  const { tenant } = useAuth();
  if (!tenant || tenant.plan !== 'trial') return null;

  const trialEnd = tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / 86400000)) : 0;
  const expired  = daysLeft === 0;

  if (!trialEnd) return null;

  return (
    <div style={{
      background: expired ? '#2d1515' : daysLeft <= 3 ? '#2d1f0a' : '#1a1f35',
      borderBottom: `1px solid ${expired ? '#ef4444' : daysLeft <= 3 ? '#f59e0b' : '#6c58f0'}`,
      padding: '8px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      fontSize: '13px',
    }}>
      <span style={{ color: expired ? '#ef4444' : daysLeft <= 3 ? '#f59e0b' : '#a5b4fc' }}>
        {expired
          ? '⚠️ Your free trial has ended. Upgrade to keep using Leadex.'
          : `⏳ Free trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`}
      </span>
      <button
        onClick={() => window.location.href='/billing'}
        style={{
          padding: '4px 14px',
          borderRadius: 6,
          border: 'none',
          background: expired ? '#ef4444' : '#6c58f0',
          color: '#fff',
          fontWeight: 600,
          fontSize: '12px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}>
        {expired ? 'Upgrade Now' : 'Upgrade Plan'}
      </button>
    </div>
  );
}

function AppShell({ children }) {
  const { user, tenant, logout, can, viewAsActive, viewAsName, exitViewAs } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const pageInfo = PAGE_META[location.pathname] || { title: 'Leadex WorkOps', sub: '' };

  // New account: add the standard Master Data once, on the owner's first login
  const seedingRef = React.useRef(false);
  useEffect(() => {
    if (seedingRef.current || viewAsActive) return;
    if (user?.role !== 'owner' || !tenant || tenant.defaults_seeded !== false) return;
    seedingRef.current = true;
    seedStandardMasterData()
      .then(() => api.post('/tenant/defaults-seeded'))
      .catch(() => { seedingRef.current = false; });
  }, [user, tenant, viewAsActive]);

  useEffect(() => {
    const company = tenant?.brand_name || tenant?.name || '';
    document.title = company ? `Qlicxo WorkOps — ${company}` : 'Qlicxo WorkOps';
  }, [tenant]);

  const close = () => setSidebarOpen(false);
  const initials = user?.initials || (user?.name || 'U').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)', fontFamily:'DM Sans,sans-serif' }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:0.5} }

        /* ── NAV HOVER ── */
        a[href]:not([class*="active"]):hover {
          background: var(--bg3) !important;
          color: var(--text) !important;
        }

        /* ── SCROLLBAR ── */
        #sidebar-scroll::-webkit-scrollbar { width: 0; }
        #sidebar-scroll:hover::-webkit-scrollbar { width: 3px; }
        #sidebar-scroll:hover::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

        /* ── TOPBAR SEARCH ── */
        .topbar-search { display:flex; align-items:center; gap:8px; padding:8px 14px; background:var(--bg3); border:1px solid var(--border); border-radius:8px; min-width:240px; transition:all 0.2s; }
        .topbar-search:focus-within { border-color:var(--accent); background:var(--bg2); box-shadow:0 0 0 3px rgba(59,126,240,0.12); }
        .topbar-search input { background:transparent; border:none; outline:none; color:var(--text); font-size:0.82rem; font-family:DM Sans,sans-serif; width:100%; }
        .topbar-search input::placeholder { color:var(--text3); }

        /* ── ICON BTN ── */
        .icon-btn { display:flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:8px; border:1px solid var(--border); background:var(--bg3); cursor:pointer; color:var(--text2); transition:all 0.15s; }
        .icon-btn:hover { border-color:var(--accent); color:var(--accent); background:rgba(59,126,240,0.06); }

        /* ── MOBILE ── */
        @media (max-width: 767px) {
          #app-sidebar { transform: translateX(-100%); }
          #app-sidebar.open { transform: translateX(0); }
          #app-main { margin-left: 0 !important; width: 100% !important; }
          .hamburger { display: flex !important; }
          .topbar-search { min-width: 140px; }
        }
      `}</style>

      {/* ── SIDEBAR OVERLAY (mobile) ── */}
      {sidebarOpen && (
        <div onClick={close} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:998, backdropFilter:'blur(2px)' }} />
      )}

      {/* ── SIDEBAR ── */}
      <aside id="app-sidebar" className={sidebarOpen ? 'open' : ''} style={{
        width: sidebarCollapsed ? 64 : 240,
        minWidth: sidebarCollapsed ? 64 : 240,
        background:'var(--bg2)', borderRight:'1px solid var(--border)',
        display:'flex', flexDirection:'column', height:'100vh',
        position:'fixed', left:0, top:0, zIndex:999,
        transition:'width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflow:'visible',
      }}>
        {/* Brand + Collapse toggle */}
        <div style={{ padding: sidebarCollapsed ? '16px 0' : '16px 16px 14px', borderBottom:'1px solid var(--border)', flexShrink:0, display:'flex', alignItems:'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', gap:8, overflow:'hidden' }}>
          {!sidebarCollapsed && (
            tenant?.brand_logo
              ? <img src={tenant.brand_logo} alt="Logo" style={{ maxHeight:32, maxWidth:130, objectFit:'contain', display:'block' }} />
              : (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ color:'#fff', fontWeight:800, fontSize:'0.85rem', fontFamily:'Fraunces,serif' }}>L</span>
                  </div>
                  <div>
                    <div style={{ fontFamily:'Fraunces,serif', fontWeight:700, fontSize: (tenant?.brand_name || 'Leadex').length > 18 ? '0.72rem' : (tenant?.brand_name || 'Leadex').length > 12 ? '0.82rem' : '0.95rem', color:'var(--text)', lineHeight:1.3, whiteSpace:'normal', wordBreak:'break-word' }}>{tenant?.brand_name || 'Leadex'}</div>
                    <div style={{ fontSize:'0.6rem', color:'var(--text3)' }}>{tenant?.brand_tagline || 'WorkOps'}</div>
                  </div>
                </div>
              )
          )}
          {sidebarCollapsed && (
            <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontWeight:800, fontSize:'0.85rem', fontFamily:'Fraunces,serif' }}>L</span>
            </div>
          )}

        </div>

        {/* Nav links */}
        <div id="sidebar-scroll" style={{ flex:1, overflowY:'auto', overflowX:'hidden', paddingBottom:8 }}>
          {NAV_SECTIONS.map(section => {
            const visibleItems = section.items.filter(item => {
              if (!item.permissionKey) return true;   // no permission needed
              if (item.ownerOnly) return can('*');    // owner-only pages
              return can(item.permissionKey);
            });
            if (visibleItems.length === 0) return null;
            return (
              <NavSection key={section.section} label={section.section} collapsed={sidebarCollapsed}>
                {visibleItems.map(item => (
                  <NavItem
                    key={item.key}
                    to={item.path}
                    icon={NAV_ICONS[item.icon] || item.icon}
                    label={item.label}
                    collapsed={sidebarCollapsed}
                    onClick={close}
                  />
                ))}
              </NavSection>
            );
          })}
        </div>

        {/* ── Collapse toggle — floating tab at sidebar edge ── */}
        <button
          onClick={() => setSidebarCollapsed(v => !v)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            position:'absolute',
            top:'50%',
            right:-14,
            transform:'translateY(-50%)',
            width:14,
            height:48,
            borderRadius:'0 8px 8px 0',
            border:'1px solid var(--border)',
            borderLeft:'none',
            background:'var(--bg2)',
            cursor:'pointer',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            color:'var(--text3)',
            zIndex:1001,
            transition:'all 0.15s',
            boxShadow:'2px 0 8px rgba(0,0,0,0.08)',
            padding:0,
          }}
          onMouseOver={e=>{e.currentTarget.style.background='var(--accent)';e.currentTarget.style.color='#fff';e.currentTarget.style.borderColor='var(--accent)';}}
          onMouseOut={e=>{e.currentTarget.style.background='var(--bg2)';e.currentTarget.style.color='var(--text3)';e.currentTarget.style.borderColor='var(--border)';}}>
          <svg viewBox="0 0 6 10" width="6" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            {sidebarCollapsed
              ? <path d="M1 1l4 4-4 4"/>
              : <path d="M5 1L1 5l4 4"/>}
          </svg>
        </button>

        {/* User footer */}
        <div style={{ padding: sidebarCollapsed ? '12px 4px' : '12px 16px', borderTop:'1px solid var(--border)', flexShrink:0 }}>
          {sidebarCollapsed ? (
            /* Collapsed: icon buttons only */
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div title={user?.name} style={{ width:34, height:34, borderRadius:8, background: user?.color || 'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.78rem', fontWeight:700, color:'#fff', cursor:'default' }}>
                {initials}
              </div>
              <button onClick={toggleTheme} title={theme==='dark'?'Light Mode':'Dark Mode'}
                style={{ width:34, height:34, borderRadius:6, border:'1px solid var(--border)', background:'var(--bg3)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.85rem', color:'var(--text3)' }}>
                {theme === 'dark' ? '☀' : '🌙'}
              </button>
              <button onClick={logout} title="Sign Out"
                style={{ width:34, height:34, borderRadius:6, border:'1px solid var(--border)', background:'var(--bg3)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.85rem', color:'var(--text3)' }}>
                ↩
              </button>
            </div>
          ) : (
            /* Expanded: full footer */
            <>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', marginBottom:8 }}>
                <div style={{ width:34, height:34, borderRadius:8, background: user?.color || 'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.78rem', fontWeight:700, color:'#fff', flexShrink:0 }}>
                  {initials}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user?.name}</div>
                  <div style={{ fontSize:'0.68rem', color:'var(--text3)', textTransform:'capitalize' }}>{user?.role}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={toggleTheme} style={{ flex:1, padding:'6px 8px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text3)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                  onMouseOver={e=>e.currentTarget.style.color='var(--text)'} onMouseOut={e=>e.currentTarget.style.color='var(--text3)'}>
                  {theme === 'dark' ? '☀ Light' : '🌙 Dark'}
                </button>
                <button onClick={logout} style={{ flex:1, padding:'6px 8px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text3)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                  onMouseOver={e=>e.currentTarget.style.color='var(--red)'} onMouseOut={e=>e.currentTarget.style.color='var(--text3)'}>
                  ↩ Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div id="app-main" style={{ marginLeft: sidebarCollapsed ? 64 : 240, flex:1, display:'flex', flexDirection:'column', minHeight:'100vh', overflow:'hidden', width:`calc(100% - ${sidebarCollapsed ? 64 : 240}px)`, transition:'margin-left 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)' }}>

        {/* ── TOPBAR ── */}
        <header style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0 24px', height:60, minHeight:60,
          background:'var(--bg2)', borderBottom:'1px solid var(--border)',
          position:'sticky', top:0, zIndex:100, gap:16,
        }}>
          {/* Left: hamburger + page title */}
          <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
            <button className="hamburger" onClick={() => setSidebarOpen(true)}
              style={{ display:'none', alignItems:'center', justifyContent:'center', width:36, height:36, background:'none', border:'none', cursor:'pointer', color:'var(--text)', fontSize:20, flexShrink:0 }}>
              ☰
            </button>
            <div style={{ minWidth:0 }}>
              <div style={{ fontFamily:'Fraunces,serif', fontWeight:700, fontSize:'1rem', color:'var(--text)', lineHeight:1.2 }}>{pageInfo.title}</div>
              <div style={{ fontSize:'0.7rem', color:'var(--text3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{pageInfo.sub}</div>
            </div>
          </div>

          {/* Right: search + notif + avatar */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
            <div className="topbar-search">
              <span style={{ color:'var(--text3)', flexShrink:0, display:'flex' }}>{NAV_ICONS.search}</span>
              <input placeholder="Search anything..." />
            </div>

            {/* Notifications */}
            <div style={{ position:'relative' }}>
              <button className="icon-btn" onClick={() => setNotifOpen(v=>!v)}>
                {NAV_ICONS.bell}
                <span style={{ position:'absolute', top:6, right:6, width:8, height:8, borderRadius:'50%', background:'var(--red)', border:'1.5px solid var(--bg2)' }} />
              </button>
              {notifOpen && (
                <div onClick={() => setNotifOpen(false)} style={{ position:'fixed', inset:0, zIndex:999 }}>
                  <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', top:46, right:0, width:300, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.2)', animation:'fadeIn 0.15s ease', zIndex:1000, overflow:'hidden' }}>
                    <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontWeight:700, fontSize:'0.85rem' }}>Notifications</span>
                      <span style={{ fontSize:'0.7rem', color:'var(--accent)', cursor:'pointer' }}>Mark all read</span>
                    </div>
                    <div style={{ padding:'32px 16px', textAlign:'center', color:'var(--text3)', fontSize:'0.82rem' }}>
                      <div style={{ fontSize:'1.8rem', marginBottom:8 }}>🔔</div>
                      No new notifications
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px 4px 4px', border:'1px solid var(--border)', borderRadius:24, background:'var(--bg3)', cursor:'pointer' }}
              title={user?.name}>
              <div style={{ width:28, height:28, borderRadius:'50%', background: user?.color || 'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, color:'#fff', flexShrink:0 }}>
                {initials}
              </div>
              <div style={{ lineHeight:1.1 }}>
                <div style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text)', whiteSpace:'nowrap', maxWidth:100, overflow:'hidden', textOverflow:'ellipsis' }}>{user?.name}</div>
                <div style={{ fontSize:'0.62rem', color:'var(--text3)', textTransform:'capitalize' }}>{user?.role}</div>
              </div>
            </div>
          </div>
        </header>


        {/* ── VIEW AS BANNER ── */}
        {viewAsActive && (
          <div style={{ background:'#7c3aed', color:'#fff', padding:'9px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, fontSize:'13px', zIndex:100, flexShrink:0 }}>
            <span>👁 <strong>View As Mode:</strong> You are seeing the system as <strong>{viewAsName}</strong></span>
            <button onClick={exitViewAs} style={{ padding:'4px 16px', borderRadius:6, border:'1px solid rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.15)', color:'#fff', fontWeight:600, fontSize:'12px', cursor:'pointer', whiteSpace:'nowrap' }}>✕ Exit View</button>
          </div>
        )}
        {/* ── TRIAL BANNER ── */}
        <TrialBanner />
        {/* ── PAGE CONTENT ── */}
        <main style={{ flex:1, overflowY:'auto', overflowX:'hidden', padding:'24px', minHeight:0, scrollBehavior:'smooth' }}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/cancel-deletion" element={<CancelDeletion />} />
          <Route path="/select/:token" element={<DaySelect />} />
          <Route path="/review/:token" element={<ReviewApproval />} />
          <Route path="/*" element={
            <PrivateRoute>
              <AppShell>
                <Routes>
                  <Route path="/dashboard"       element={<Dashboard />} />
                  <Route path="/work-orders"     element={<WorkOrders />} />
                  <Route path="/planning"        element={<Planning />} />
                  <Route path="/special-days"   element={<SpecialDays />} />
                  <Route path="/pipeline"        element={<Pipeline />} />
                  <Route path="/content"         element={<ContentCalendar />} />
                  <Route path="/creatives"       element={<Creatives />} />
                  <Route path="/postcopy"        element={<PostCopy />} />
                  <Route path="/videoworks"       element={<VideoWorks />} />
                  <Route path="/printmedia"        element={<PrintMedia />} />
                  <Route path="/scheduling"         element={<Scheduling />} />
                  <Route path="/published-content"  element={<PublishedContent />} />
                  <Route path="/monitoring"      element={<Monitoring />} />
                  <Route path="/assignments"     element={<WorkAssignments />} />
                  <Route path="/reallocation"    element={<Reallocation />} />
                  <Route path="/touchpoints"     element={<Touchpoints />} />
                  <Route path="/approvals"      element={<Approvals />} />
                  <Route path="/chat"           element={<Chat />} />
                  <Route path="/kpi"             element={<KPI />} />
                  <Route path="/performance"     element={<Performance />} />
                  <Route path="/costs"           element={<Costs />} />
                  <Route path="/team"            element={<Team />} />
                  <Route path="/clients"         element={<Clients />} />
                  <Route path="/masterdata"      element={<MasterData />} />
                  <Route path="/settings"        element={<Settings />} />
                  <Route path="/billing"         element={<Billing />} />
                  <Route path="/access-control"  element={<AccessControl />} />
                  <Route path="/"                element={<Navigate to="/dashboard" />} />
                </Routes>
              </AppShell>
            </PrivateRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}